import {
  Box,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { memo, useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import colors from "app/colors";
import UserMention from "modules/_shared/mentions/user-mention";
import UserAvatar from "modules/_shared/user-avatar";
import { useAuth } from "modules/providers/auth-provider";
import { useUsers } from "modules/providers/users-provider";
import { getUserFullName } from "modules/utils/Functions";

interface IProps {
  accessUserIdList: string[];
  activeUserIdList: string[];
}

type ParticipantsListRow =
  | { kind: "participant"; userId: string }
  | { kind: "invited-header"; count: number };

const ParticipantListRow = memo(({ userId }: { userId: string }) => {
  const { usersById } = useUsers();

  return (
    <Flex
      align="flex-start"
      gap={3}
      py={3}
      borderBottom={`1px solid ${colors.gray0}`}
    >
      <UserAvatar
        userId={userId}
        size="sm"
        boxSizePx="36px"
        borderRadiusPx="18px"
        displayOnlineIndicator={false}
      />
      <VStack spacing="2px" alignItems="flex-start" flex={1} minW={0}>
        <UserMention
          id={userId}
          overrideColor="var(--blue-100, #007AFF)"
          fontFamily="Inter"
          fontSize="14px"
          fontStyle="normal"
          fontWeight={700}
          lineHeight="normal"
          letterSpacing="-0.28px"
        />
        <Text
          fontSize={12}
          color={colors.gray50}
          fontWeight={400}
          textAlign="left"
          w="full"
        >
          {getUserFullName(usersById[userId])}
        </Text>
      </VStack>
    </Flex>
  );
});

const VideoCallParticipantsDrawer: React.FC<IProps> = ({
  accessUserIdList,
  activeUserIdList,
}) => {
  const { me } = useAuth();

  const activeSetIncludingSelf = useMemo(() => {
    const next = new Set(activeUserIdList);
    if (me?.id) {
      next.add(me.id);
    }
    return next;
  }, [activeUserIdList, me?.id]);

  const activeUserIdsOrdered = useMemo(() => {
    const ordered = [...new Set(activeUserIdList)];
    if (me?.id && !ordered.includes(me.id)) {
      ordered.push(me.id);
    }
    return ordered;
  }, [activeUserIdList, me?.id]);

  const invitedNotOnCallIds = useMemo(
    () => accessUserIdList.filter((id) => !activeSetIncludingSelf.has(id)),
    [accessUserIdList, activeSetIncludingSelf],
  );

  const virtuosoData = useMemo((): ParticipantsListRow[] => {
    const rows: ParticipantsListRow[] = activeUserIdsOrdered.map((userId) => ({
      kind: "participant",
      userId,
    }));
    if (invitedNotOnCallIds.length > 0) {
      rows.push({
        kind: "invited-header",
        count: invitedNotOnCallIds.length,
      });
      for (const userId of invitedNotOnCallIds) {
        rows.push({ kind: "participant", userId });
      }
    }
    return rows;
  }, [activeUserIdsOrdered, invitedNotOnCallIds]);

  const computeItemKey = useCallback(
    (_index: number, row: ParticipantsListRow) =>
      row.kind === "invited-header"
        ? "invited-header"
        : `participant-${row.userId}`,
    [],
  );

  const itemContent = useCallback((_index: number, row: ParticipantsListRow) => {
    if (row.kind === "invited-header") {
      return (
        <Box pt={4} pb={2}>
          <Text
            color="var(--neutral-60, #848484)"
            fontFamily="Inter"
            fontSize="14px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="normal"
            letterSpacing="-0.28px"
            textTransform="uppercase"
          >
            INVITED BUT NOT ON CALL({row.count})
          </Text>
        </Box>
      );
    }
    return <ParticipantListRow userId={row.userId} />;
  }, []);

  return (
    <DrawerContent
      display="flex"
      flexDirection="column"
      overflow="hidden"
      h="100vh"
      maxH="100vh"
    >
      <DrawerCloseButton top={3} left={5} color={colors.blackSecondary} />
      <DrawerHeader flexShrink={0} pb={2}>
        <Text
          fontFamily="Outfit"
          fontSize={20}
          fontWeight={700}
          textAlign="center"
          w="full"
        >
          Call Participants ({accessUserIdList.length})
        </Text>
      </DrawerHeader>
      <DrawerBody
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        overflow="hidden"
        p={0}
        px={6}
        pt={2}
        pb={5}
      >
        <Box
          flex="1"
          minH={0}
          minW={0}
          w="full"
          borderTop={`1px solid ${colors.blackSecondary}`}
        >
          <Virtuoso
            data={virtuosoData}
            style={{ height: "100%", width: "100%" }}
            computeItemKey={computeItemKey}
            itemContent={itemContent}
          />
        </Box>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(VideoCallParticipantsDrawer);
